// Per spec/components.md §1. **P-037.8 redesign** (Banks's PSD audit;
// **P-037.9 simplification** — D-109): the `size` and `ctaStyle` props
// were dropped per Banks's design audit:
//
// - `size` removed — both `'large'` (h2/24) and `'medium'` (h3/18) had
//   been folded to 21 px since P-037.8, so the enum carried no visual
//   variation. Always renders `<h2>`. Sub-section-heading content moves
//   to the dedicated `SubSectionHeader` Component (h3, 14 px gray).
// - `ctaStyle` removed — Banks confirmed the chapter only uses link-style
//   CTAs. The Button-primitive arm is gone; the CTA always renders as the
//   14 px Helvetica bold `#c69b34` inline anchor.
//
// Layout (unchanged from P-037.8):
// - Full-width rule above (`paddingY: 0` via Divider primitive)
// - 50 px gap below rule
// - Heading row: 30 px L/R padding, 16 px bottom padding
// - CTA arm: heading in left cell (30 px L padding), CTA in right cell
//   (30 px R padding); both 50 px top / 16 px bottom
//
// Props use a discriminated union (D-033) — `ctaUrl` is paired-required
// with `ctaLabel` at the Component layer. The `section-heading` Block
// (D-108) relaxes to independently optional and only forwards an atomic
// CTA when both values are present.

import { html, toAbsoluteUrl } from '../render'
import { heading } from '../primitives/heading'
import { tokens } from '../tokens'
import { HtmlString, EMPTY_HTML } from '../types'

interface SectionHeaderBase {
  heading: string
  /** Template-level link color from `RenderContext.linkColor` (P-037.18
   *  D-118). Used as the CTA anchor color when the CTA arm renders.
   *  Defaults to `tokens.color['link-default']` (the fallback used when
   *  no Header block is in the Email). */
  linkColor?: string
  /** Template-level subheader banner URL from
   *  `RenderContext.subheaderImageUrl` (P-037.19 D-125). When provided,
   *  the Component inserts a decorative 300 × 35 banner row between the
   *  full-width rule and the heading row, anchored to the right edge of
   *  the content area. When the banner renders, the heading row's top
   *  padding shrinks by `BANNER_HEIGHT` so the heading + CTA stay at the
   *  same y-position as the no-banner layout (Banks's spacing ask). Only
   *  the `section-heading` Block passes this; other SectionHeader
   *  consumers (day-chairs, chapter-sponsors, member-list, …) leave it
   *  unset. */
  subheaderImageUrl?: string
}

export type SectionHeaderProps =
  | (SectionHeaderBase & { ctaLabel?: undefined; ctaUrl?: undefined })
  | (SectionHeaderBase & { ctaLabel: string; ctaUrl: string })

const HEADING_SIZE_PX = 21
// P-037.18 D-118 — ≥18 px headlines render in `accent-navy-deep` (#041e42)
// per the system-wide convention. SectionHeader's heading is 21 px, so it
// falls under the rule. Previously #000000 (P-037.8 D-108).
const HEADING_COLOR = tokens.color['accent-navy-deep']
const PAD_X = 30
// P-037.29 post-Phase-Close §item B — 20 px of breathing space ABOVE
// the divider rule, baked into the Component (was: registry section-
// heading.top = 20 added the spacer outside the Block per D-137 §item 2;
// that worked in composition but the Block Gallery's isolated preview
// doesn't fire the system spacer, so the rule was rendering flush at
// the Block top in isolation). Moving the 20 px INSIDE the Component
// guarantees it shows up in both isolated and composed rendering. The
// registry drops section-heading.top to 0 in lockstep so the spacing
// isn't double-counted in composition.
const PAD_ABOVE_RULE = 20
const PAD_TOP = 50
// P-037.19 D-125 — when the subheader banner row renders, the heading
// row's top padding shrinks to absorb the 35 px banner height so the
// heading + CTA stay at the same y-position as the no-banner layout.
// `PAD_TOP_WITH_BANNER (15) + BANNER_HEIGHT (35) = PAD_TOP (50)`.
const PAD_TOP_WITH_BANNER = 15
// P-037.29 D-137 §item 2 — `PAD_BOTTOM` dropped from 16 → 0. The
// system-level inter-Block spacer (`src/render/block-padding.ts`)
// owns the gap from the heading text bottom to the next Block — 50 px
// for section-heading per Banks's convention. The Component emits 0
// below the heading text so the spacer measurement is honest.
const PAD_BOTTOM = 0
const CTA_SIZE_PX = 14
// P-037.19 D-125 — subheader banner dimensions. Fixed 300 × 35 per Banks's
// source PSDs; banner asset is one of 7 per-Header-style PNGs served from
// `public/ypo-s7-email-subheader-*.png`. Same naming family as the Header
// PNGs (D-102). Banner is decorative (color-coded chevron bar) → `alt=""`.
const BANNER_WIDTH = 300
const BANNER_HEIGHT = 35

export function sectionHeader(props: SectionHeaderProps): HtmlString {
  const headingHtml = heading({
    text: props.heading,
    level: 2,
    color: HEADING_COLOR,
    size: HEADING_SIZE_PX,
  })

  // P-037.19 D-125 — banner row + heading top-padding compensation. When
  // a subheaderImageUrl is threaded in, render a 300 × 35 right-aligned
  // banner row between the rule and the heading row, and shrink heading
  // PAD_TOP so the heading + CTA stay at the no-banner y-position.
  // P-037.26 D-134 — derive the absolutized banner URL up-front so both
  // arms reuse it. `hasBanner` reads from the derived string (avoids a
  // separate TS-narrowing seam on `props.subheaderImageUrl`).
  const absoluteBannerUrl =
    props.subheaderImageUrl === undefined ? '' : toAbsoluteUrl(props.subheaderImageUrl)
  const hasBanner = absoluteBannerUrl !== ''
  const headingPadTop = hasBanner ? PAD_TOP_WITH_BANNER : PAD_TOP

  // P-037.29 post-Phase-Close §item B — 20 px of breathing space ABOVE
  // the divider rule.
  //
  // **P-048.10 / D-153 — `padding-top` on the divider row's cell**
  // (was a separate contentless `<tr><td height="20">&nbsp;</td></tr>`
  // row pre-P-048.10). Banks's Outlook smoke after P-048.9 showed
  // residual gray gaps specifically ABOVE SectionHeader blocks. The
  // contentless `<td>` pattern leaks body bg in Outlook (same root
  // cause as P-048.6 / P-048.8 inter-Block spacer cells: Outlook
  // skips bg paint on cells with no substantial content). Refined
  // fix: drop the contentless padAboveRule row entirely; move the
  // 20 px into `padding-top` of the divider row's outer `<td>`. The
  // divider row's cell has substantial content (the nested rule-
  // table with `border-top`), so Outlook cascade-paints the cell
  // white including the padding-top area. The CTA-arm divider was
  // already hand-rolled inline; the no-CTA arm switches from
  // `divider({paddingY: 0})` primitive call to the same hand-rolled
  // pattern with `padding-top: 20px;` on the outer `<td>` (the
  // primitive is unchanged; only SectionHeader's usage diverges).

  if (props.ctaLabel === undefined) {
    // No CTA: outer table is single-column. Divider hand-rolled with
    // `padding-top: ${PAD_ABOVE_RULE}px;` on the outer `<td>` (was the
    // primitive call + a separate padAboveRule row).
    // prettier-ignore
    const dividerHtml = html`<tr><td style="padding-top: ${PAD_ABOVE_RULE}px;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td style="border-top: 1px solid #d8dde3; font-size: 0; line-height: 0;">&nbsp;</td></tr></table></td></tr>`
    // Banner row is right-anchored via THREE belt-and-suspenders layers
    // — necessary because no single mechanism covers every email client:
    //   1. `align="right"` HTML attr on the inner `<table>` — honored by
    //      Outlook desktop's Word renderer + Apple Mail.
    //   2. `align="right"` HTML attr on the OUTER `<td>` — additional
    //      fallback for clients that ignore the inner-table `align=`.
    //   3. `style="margin-left: auto; margin-right: 0;"` inline CSS on
    //      the inner table — for clients that ignore HTML `align=` but
    //      honor CSS margins (Android Outlook app, Gmail iOS app's
    //      stricter render paths). P-048.4 §A / D-147 added this layer
    //      after Banks's Litmus Android Outlook preview showed the
    //      banner left-aligning despite the inner-table `align="right"`.
    // Outer `<td>` carries zero padding so the banner sits flush against
    // the right edge of the 680 px container (Banks's carry-forward note
    // post-P-037.19 commit `84da41e`: "the image should be all the way to
    // the right side of the 680 block, not padded in").
    // prettier-ignore
    const bannerRow = hasBanner
      ? html`<tr><td align="right" style="padding: 0; line-height: 0; font-size: 0;"><table role="presentation" cellpadding="0" cellspacing="0" border="0" align="right" style="margin-left: auto; margin-right: 0;"><tr><td style="line-height: 0; font-size: 0;"><img src="${absoluteBannerUrl}" alt="" width="${BANNER_WIDTH}" height="${BANNER_HEIGHT}" style="display: block; width: ${BANNER_WIDTH}px; height: ${BANNER_HEIGHT}px; border: 0; outline: none; text-decoration: none;"></td></tr></table></td></tr>`
      : EMPTY_HTML
    // prettier-ignore
    return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">${dividerHtml}${bannerRow}<tr><td style="padding: ${headingPadTop}px ${PAD_X}px ${PAD_BOTTOM}px ${PAD_X}px;">${headingHtml}</td></tr></table>`
  }

  const { ctaLabel, ctaUrl } = props
  const ctaColor = props.linkColor ?? tokens.color['link-default']
  const absoluteCtaUrl = toAbsoluteUrl(ctaUrl)
  // CTA anchor: color + weight + decoration on the anchor; font-family +
  // font-size on the wrapping `<td>` so the anchor inherits them. Color
  // is template-level per ctx.linkColor (P-037.18 D-118) — fallback to
  // tokens.color['link-default'] for callers (tests, gallery synthetic
  // envelopes) that don't thread a linkColor.
  // prettier-ignore
  const ctaHtml = html`<a href="${absoluteCtaUrl}" target="_blank" rel="noopener noreferrer" style="font-family: ${tokens.type.fontFamily}; color: ${ctaColor}; font-weight: ${tokens.type.weight.bold}; text-decoration: none;">${ctaLabel}</a>`

  // CTA arm: the heading row has 2 cells, so the table has 2 columns. The
  // divider's `<td>` must `colspan="2"` or the rule only stretches to the
  // heading/CTA column boundary (Banks's P-037.10 audit — the rule was
  // visibly stopping short above the CTA). Hand-rolled inline rather than
  // adding a `colspan` prop to the `divider` primitive — table-layout
  // concerns shouldn't bleed into the primitive's surface.
  //
  // P-048.10 / D-153 — `padding-top: 20px;` (was `padding: 0;`) absorbs
  // the prior separate contentless padAboveRuleCta row. Same Outlook
  // cascade-to-cell fix as the no-CTA arm.
  // prettier-ignore
  const dividerWithColspanHtml = html`<tr><td colspan="2" style="padding: ${PAD_ABOVE_RULE}px 0 0 0;"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td style="border-top: 1px solid #d8dde3; font-size: 0; line-height: 0;">&nbsp;</td></tr></table></td></tr>`

  // CTA arm banner row: same right-anchored inner-table pattern as the
  // no-CTA arm (flush against the 680 px right edge — zero outer padding),
  // with `colspan="2"` on the outer `<td>` so it spans both heading + CTA
  // columns. P-048.4 §A / D-147 — same 3-layer right-align belt-and-
  // suspenders as the no-CTA arm: outer `<td align="right">` + inner
  // `<table align="right">` + inner-table inline `margin-left: auto;
  // margin-right: 0;`.
  // prettier-ignore
  const bannerRowCta = hasBanner
    ? html`<tr><td colspan="2" align="right" style="padding: 0; line-height: 0; font-size: 0;"><table role="presentation" cellpadding="0" cellspacing="0" border="0" align="right" style="margin-left: auto; margin-right: 0;"><tr><td style="line-height: 0; font-size: 0;"><img src="${absoluteBannerUrl}" alt="" width="${BANNER_WIDTH}" height="${BANNER_HEIGHT}" style="display: block; width: ${BANNER_WIDTH}px; height: ${BANNER_HEIGHT}px; border: 0; outline: none; text-decoration: none;"></td></tr></table></td></tr>`
    : EMPTY_HTML

  // prettier-ignore
  return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%">${dividerWithColspanHtml}${bannerRowCta}<tr><td style="padding: ${headingPadTop}px 0 ${PAD_BOTTOM}px ${PAD_X}px;" valign="middle">${headingHtml}</td><td style="padding: ${headingPadTop}px ${PAD_X}px ${PAD_BOTTOM}px 0; font-family: ${tokens.type.fontFamily}; font-size: ${CTA_SIZE_PX}px;" align="right" valign="middle">${ctaHtml}</td></tr></table>`
}
